const STATUS_BADGE_RULES = [
    ["bg-success text-white", ["completed"]],
    ["bg-danger-subtle text-danger-emphasis", ["declined"]],
    ["bg-warning-subtle text-warning-emphasis", ["opened"]],
    ["bg-primary-subtle text-primary", ["sent", "awaiting", "pending"]]
];

const TIMELINE_LABEL_RULES = [
    { label: "Declined", recipient_key: "declined_at", matches: ["declined"] },
    { label: "Signed", recipient_key: "completed_at", matches: ["completed"] },
    { label: "Opened", recipient_key: "opened_at", matches: ["opened"] },
    { label: "Sent", recipient_key: "sent_at", matches: ["sent", "awaiting", "pending"] }
];

function normalize_text(value) {
    return String(value || "").trim();
}

export function format_status_label(value) {
    const normalized_status = normalize_text(value).toLowerCase();
    if (!normalized_status) {
        return "Pending";
    }

    return normalized_status
        .split("_")
        .filter(Boolean)
        .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
        .join(" ");
}

export function format_display_date(value) {
    const raw_value = normalize_text(value);
    if (!raw_value) {
        return "--";
    }

    const parsed_date = new Date(raw_value);
    if (Number.isNaN(parsed_date.getTime())) {
        return raw_value;
    }

    return parsed_date.toLocaleString(undefined, {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit"
    });
}

function get_matching_status_badge_class(normalized_status) {
    const matching_rule = STATUS_BADGE_RULES.find(([, matches]) => (
        matches.some((match) => normalized_status.includes(match))
    ));
    return matching_rule?.[0] || "bg-secondary-subtle text-secondary-emphasis";
}

export function get_status_badge_class(status) {
    const normalized_status = normalize_text(status).toLowerCase();
    return get_matching_status_badge_class(normalized_status);
}

function recipient_matches_timeline_rule(recipient = {}, status_key = "", rule = {}) {
    const has_timeline_date = Boolean(recipient?.[rule.recipient_key]);
    const matches_status = rule.matches.some((match) => status_key.includes(match));
    const matched = has_timeline_date || matches_status;
    return matched;
}

function get_timeline_palette(step_state) {
    if (step_state === "completed") {
        const timeline_colors = {
            circle_class: "bg-success text-white",
            connector_class: "border-success",
            label_class: "text-success"
        };
        return timeline_colors;
    }

    if (step_state === "current") {
        const step_color = {
            circle_class: "bg-primary text-white",
            connector_class: "border-primary",
            label_class: "text-primary"
        };
        return step_color;
    }

    if (step_state === "declined") {
        const decline_step_color = {
            circle_class: "bg-danger text-white",
            connector_class: "border-danger",
            label_class: "text-danger"
        };
        return decline_step_color;
    }

    const default_step_color = {
        circle_class: "bg-light text-muted border",
        connector_class: "border-secondary-subtle",
        label_class: "text-body-secondary"
    };
    return default_step_color;
}

function get_current_timeline_label(recipient, status_key) {
    const matched_rule = TIMELINE_LABEL_RULES.find((rule) => (
        recipient_matches_timeline_rule(recipient, status_key, rule)
    ));
    const timeline_label = matched_rule?.label || "";
    return timeline_label;
}

function timeline_is_declined_step(label, current_label) {
    return label === "Declined" && current_label === "Declined";
}

function get_signed_timeline_step_state(step_index, current_index) {
    return step_index <= current_index ? "completed" : "upcoming";
}

function get_in_progress_timeline_step_state(step_index, current_index) {
    return step_index < current_index
        ? "completed"
        : (step_index === current_index ? "current" : "upcoming");
}

function get_resolved_timeline_step_state(current_label, step_index, current_index) {
    return current_label === "Signed"
        ? get_signed_timeline_step_state(step_index, current_index)
        : get_in_progress_timeline_step_state(step_index, current_index);
}

function get_timeline_step_state(label, current_label, step_index, current_index) {
    if (current_index === -1) {
        return "upcoming";
    }

    if (timeline_is_declined_step(label, current_label)) {
        return "declined";
    }

    return get_resolved_timeline_step_state(current_label, step_index, current_index);
}

function build_timeline_step(label, value, step_state, icon) {
    const timeline_palette = get_timeline_palette(step_state);
    const is_active_step = step_state === "completed" || step_state === "current" || step_state === "declined";
    const timeline_step = {
        label,
        date: format_display_date(value),
        icon: is_active_step ? icon : "",
        number: "",
        circle_class: timeline_palette.circle_class,
        connector_class: timeline_palette.connector_class,
        label_class: timeline_palette.label_class
    };
    return timeline_step;
}

function build_timeline(recipient, status_key, timeline_definition) {
    const current_label = get_current_timeline_label(recipient, status_key);
    const current_index = timeline_definition.findIndex(([label]) => label === current_label);

    return timeline_definition
        .map(([label, value, icon], step_index) => build_timeline_step(
            label,
            value,
            get_timeline_step_state(label, current_label, step_index, current_index),
            icon
        ));
}

function build_declined_timeline(recipient, status_key) {
    return build_timeline(recipient, status_key, [
        ["Sent", recipient.sent_at, "bi-send-fill"],
        ["Opened", recipient.opened_at, "bi-eye-fill"],
        ["Declined", recipient.declined_at, "bi-x-lg"]
    ]).filter((step) => step.date !== "--" || step.label === "Declined");
}

function build_standard_timeline(recipient, status_key) {
    return build_timeline(recipient, status_key, [
        ["Sent", recipient.sent_at, "bi-send-fill"],
        ["Opened", recipient.opened_at, "bi-eye-fill"],
        ["Signed", recipient.completed_at, "bi-check-lg"]
    ]);
}

function get_recipient_timeline(recipient, status_key) {
    return status_key.includes("declined")
        ? build_declined_timeline(recipient, status_key)
        : build_standard_timeline(recipient, status_key);
}

function get_recipient_activity_entry(recipient) {
    return [
        ["Declined", recipient.declined_at],
        ["Signed", recipient.completed_at],
        ["Opened", recipient.opened_at],
        ["Sent", recipient.sent_at]
    ].find(([, value]) => Boolean(value)) || null;
}

export function build_recipient_activity(recipient) {
    const activity_entry = get_recipient_activity_entry(recipient);
    if (activity_entry) {
        return `${activity_entry[0]} on ${format_display_date(activity_entry[1])}`;
    }

    return "Waiting for signer activity.";
}

export function build_recipient_timeline(recipient, status_key) {
    return get_recipient_timeline(recipient, status_key);
}
